import React, { FunctionComponent, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import { SearchIcon } from "assets";

import { useSites } from "hooks";

const ITEMS_PER_PAGE = 30;

type SiteOption = { name: string; id: number | undefined };

export const AddOtherSitesDialog: FunctionComponent<{
  handleSaveClick: (newSiteApplied: SiteOption) => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ handleSaveClick, isOpen, onClose }) => {
  const containsText = (text: any, searchText: any) =>
    typeof text === "string" &&
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const { data: sites } = useSites();
  const [selectedOption, setSelectedOption] = useState<SiteOption>({ name: "", id: undefined });
  const [visibleItemsCount, setVisibleItemsCount] = useState(ITEMS_PER_PAGE);
  const [menuOpen, setMenuOpen] = useState(false);

  const isApplyDisabled = selectedOption.name === "";

  const [searchText, setSearchText] = useState("");

  // Filter sites based on search text
  const filteredSites = useMemo(
    () => sites && sites.filter((site) => containsText(site.name, searchText)),
    [searchText, sites],
  );

  // Limit visible items for performance
  const displayedSites = useMemo(
    () => filteredSites?.slice(0, visibleItemsCount),
    [filteredSites, visibleItemsCount],
  );

  const hasMoreItems = filteredSites && filteredSites.length > visibleItemsCount;
  const totalFilteredCount = filteredSites?.length || 0;

  const handleSelectChange = (site: SiteOption) => {
    setSelectedOption(site);
    setMenuOpen(false);
  };

  const handleLoadMore = () => {
    setVisibleItemsCount((prev) => prev + ITEMS_PER_PAGE);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    // Reset visible items count when search changes
    setVisibleItemsCount(ITEMS_PER_PAGE);
  };

  const handleMenuClose = () => {
    setSearchText("");
    setVisibleItemsCount(ITEMS_PER_PAGE);
    setMenuOpen(false);
  };

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} sx={{ top: "-300px" }}>
      <DialogTitle
        sx={{
          color: "#054254",
          fontWeight: "600",
          borderBottom: "1px solid #D9D9D9",
        }}
      >
        Find More Sites
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{
            color: "#054254",
            backgroundColor: "#F2F8FF",
            borderRadius: "5px",
            marginTop: "12px",
            padding: "12px",
            fontSize: "14px",
          }}
        >
          Looking for a way to apply this bill to another site? Search for sites
          below to apply your bill to a different site.
        </DialogContentText>
        <div style={{ marginTop: "20px", color: "#525B73" }}>
          Select a site:
        </div>
        <Box mt={1}>
          <FormControl>
            <Select
              sx={{ maxHeight: "44px", width: "460px" }}
              open={menuOpen}
              onOpen={handleMenuOpen}
              onClose={handleMenuClose}
              MenuProps={{
                autoFocus: false,
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
              labelId="search-select-label"
              id="search-select"
              placeholder="Select"
              value={selectedOption.name}
              renderValue={() => selectedOption.name || "Select a site"}
            >
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search for a site"
                  fullWidth
                  value={searchText}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    handleSearchChange(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Escape") {
                      e.stopPropagation();
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              </ListSubheader>
              {displayedSites && displayedSites.length > 0 ? (
                <>
                  {displayedSites.map((site: any) => (
                    <MenuItem
                      key={site.id}
                      value={site.name}
                      onClick={() => {
                        handleSelectChange(site);
                      }}
                    >
                      {site.name}
                    </MenuItem>
                  ))}
                  {hasMoreItems && (
                    <Box
                      component="div"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLoadMore();
                      }}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#054254",
                        "&:hover": {
                          backgroundColor: "#F2F8FF",
                        },
                      }}
                    >
                      Load more ({totalFilteredCount - visibleItemsCount} remaining)
                    </Box>
                  )}
                </>
              ) : (
                <MenuItem disabled>
                  {searchText ? "No sites found" : "No sites available"}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ color: "#6B7280", mr: "4px", mb: "10px" }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          sx={{
            backgroundColor: isApplyDisabled ? "#D1D5DB" : "#054254",
            color: isApplyDisabled ? "#FFFFFF" : "#EAFCFF",
            mr: "15px",
            mb: "10px",
            "&:hover": {
              backgroundColor: isApplyDisabled ? "#D1D5DB" : "#0781a6",
            },
            "&:focus": {
              outline: "none",
            },
          }}
          onClick={() => {
            handleSaveClick(selectedOption);
            onClose();
          }}
          disabled={isApplyDisabled}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

